/**
 * Blind nil prompt (DESIGN-client §4, C-T15).
 *
 * The scene draws the sealed hand and the wax band; this is the explainer that makes the offer
 * legible. Both choices are already available in-scene — slam the sealed stack to go blind, tap
 * the band to look — and these buttons deliberately send the *same* intents rather than opening a
 * second code path, so the two ways of answering cannot drift apart.
 *
 * The stake is on the button because it is the whole decision: ±200 is double a normal nil, and a
 * player who has never seen a blind nil has no way to know that from a wax seal alone.
 *
 * `presentation.blindNilOffer` is written by the scene's bid cue handlers (`dealt` /
 * `blindNilOffered` / `sealBroken`), since `scene/**` may not touch the store.
 */
import { useStore } from '../../store/store';
import { intents } from '../runtime';
import { Button } from '../ui';

export function BlindNilPrompt() {
  const offer = useStore((s) => s.presentation.blindNilOffer);
  const seat = useStore((s) => s.session.seat);

  // A spectator has no hand to seal, and an unsealed offer is one the player has already answered.
  if (!offer?.sealed || seat == null) return null;

  return (
    <div className="blind-nil" role="group" aria-label="Blind nil offer" data-testid="blind-nil-prompt">
      <div className="blind-nil__copy">
        <div className="blind-nil__title">You&apos;re trailing. Bid before you look?</div>
        <div className="blind-nil__hint">Win no books for +200. Take one and it&apos;s −200.</div>
      </div>
      {/*
        Looking is first and primary, going blind is second.

        The stake is the whole point of the offer and it is still on its own button, so the dare is
        as legible as it ever was. What changed is which way the default falls: this prompt appears
        unbidden, over a hand the player has not seen, and the primary button was the one that bets
        two hundred points on cards nobody has looked at. A player who taps the bright one without
        reading has now looked at their hand, which is recoverable; the other way round is not.
      */}
      <div className="row">
        <Button variant="primary" testId="blind-nil-peek" onClick={() => intents()?.peek()}>
          Look at hand
        </Button>
        <Button testId="blind-nil-go" onClick={() => intents()?.blindNil()}>
          Go blind (±200)
        </Button>
      </div>
    </div>
  );
}
